/**
 * Mobile-Safari top safe-area floor — computed once, before React mounts, and
 * re-applied on orientation change (see `installSafeTopFloorWatcher`).
 *
 * History:
 * - Original: floor keyed ONLY on `display-mode: standalone` — any installed
 *   PWA got 48px, every browser tab (including iPhone Safari) got 6px.
 * - Fix 1: ADDED an iPhone-in-browser case (iPhone Safari also sits under the
 *   status bar via `viewport-fit=cover`) — did not swap the axis.
 * - Fix 2: gated that iPhone-BROWSER case on PORTRAIT ONLY — iOS hides the
 *   status bar in landscape, so flooring there was dead space. Standalone was
 *   deliberately left orientation-blind ("an installed PWA must not move").
 * - Fix 3: Fix 2's "standalone must not move" premise was wrong for iPhone —
 *   iOS hides the status bar in landscape in BOTH modes; iPhone became purely
 *   orientation-driven (48 portrait / 6 landscape) in browser AND PWA. iPad
 *   stays standalone-only (it still shows a status bar in landscape).
 * - Fix 4 (2026-07-28, founder vpdebug on service-dev): In Safari *browser*
 *   mode, `env(safe-area-inset-top)` reports **0** while the visual viewport
 *   already sits below system/Safari chrome. Forcing the 48px floor there
 *   created a fake white band above the header (and stuck at 48 after rotate
 *   into landscape). Browser mode returns to the original 6px floor; the CSS
 *   consumer still clears a real inset via
 *   `max(calc(rhythm + env(safe-area-inset-top)), var(--cvx-safe-top-floor))`
 *   when WebKit reports one. PWA keeps Fix 3's orientation rule (env is
 *   trustworthy there when the status bar overlays the page).
 *
 * Floor rule (current):
 * - iPhone + **browser** → always 6px (trust env when non-zero)
 * - iPhone + **PWA** → 48px portrait, 6px landscape
 * - Everything else → 48px when standalone, else 6px (iPad PWA stays 48 in
 *   landscape on purpose)
 */

import { setOrientationSettlePreamble } from './orientationSettle';
import { ensureViewportSafeAreaLayoutEpochInstalled } from './viewportSafeAreaLayoutEpoch';

export const SAFE_TOP_FLOOR_IOS_PX = 48;
export const SAFE_TOP_FLOOR_DEFAULT_PX = 6;
export const SAFE_TOP_FLOOR_VAR = '--cvx-safe-top-floor';

/**
 * Pure: px floor for the given platform/display-mode/orientation
 * classification. No DOM access.
 */
export function computeSafeTopFloorPx({ isIPhone, isStandalone, isPortrait } = {}) {
  // iPhone browser: do NOT invent a 48px status-bar floor — Safari already
  // clears chrome and env-top is often 0 (Fix 4). Real insets still win in CSS.
  if (isIPhone && !isStandalone) return SAFE_TOP_FLOOR_DEFAULT_PX;
  // iPhone PWA: status bar overlays the page in portrait; hidden in landscape.
  if (isIPhone) return isPortrait ? SAFE_TOP_FLOOR_IOS_PX : SAFE_TOP_FLOOR_DEFAULT_PX;
  // Everything else: original standalone rule (iPad keeps a landscape status bar).
  return isStandalone ? SAFE_TOP_FLOOR_IOS_PX : SAFE_TOP_FLOOR_DEFAULT_PX;
}

/**
 * Detects iOS (iPhone/iPad/iPod) from a navigator-like object. Covers iPadOS
 * 13+, which masquerades as desktop Safari (`platform === 'MacIntel'`) but is
 * still a touch device (`maxTouchPoints > 1`) — a real Mac reports 0.
 * Every property access is guarded: `nav` may be undefined or partial.
 *
 * Kept broad (iPhone + iPad + iPod) on purpose — this is the general "is this
 * an Apple touch device" signal used by `ViewportDebugOverlay`'s readout, NOT
 * the safe-top floor decision. The floor uses the narrower `detectIPhone`
 * below, because iPad Safari doesn't need the status-bar floor.
 */
export function detectIOS(nav = window.navigator) {
  if (!nav) return false;
  const userAgent = typeof nav.userAgent === 'string' ? nav.userAgent : '';
  if (/iPad|iPhone|iPod/.test(userAgent)) return true;
  const platform = typeof nav.platform === 'string' ? nav.platform : '';
  const maxTouchPoints = typeof nav.maxTouchPoints === 'number' ? nav.maxTouchPoints : 0;
  return platform === 'MacIntel' && maxTouchPoints > 1;
}

/**
 * Detects iPhone/iPod specifically — NOT iPad. iPad Safari puts its chrome in
 * a top toolbar rather than overlaying the page, so it doesn't need the
 * status-bar floor even in a browser tab. Same defensive guards as `detectIOS`:
 * `nav` may be undefined or partial.
 */
export function detectIPhone(nav = window.navigator) {
  if (!nav) return false;
  const userAgent = typeof nav.userAgent === 'string' ? nav.userAgent : '';
  return /iPhone|iPod/.test(userAgent);
}

/**
 * Reproduces the original `IS_STANDALONE_PWA` check exactly: the CSS
 * `display-mode: standalone` media query OR the legacy iOS
 * `navigator.standalone` flag — no `minimal-ui`, nothing added. Every hop is
 * guarded: `win`, `win.matchMedia`, and `win.navigator` may be missing.
 */
export function detectStandaloneDisplay(win = window) {
  if (!win) return false;
  const matchesDisplayMode = typeof win.matchMedia === 'function'
    && win.matchMedia('(display-mode: standalone)').matches === true;
  const legacyIosStandalone = !!win.navigator && win.navigator.standalone === true;
  return matchesDisplayMode || legacyIosStandalone;
}

/**
 * Detects portrait orientation. Prefers the `(orientation: portrait)` media
 * query; falls back to comparing `innerHeight`/`innerWidth` when `matchMedia`
 * is unavailable; and — if neither signal is available — defaults to `true`
 * (portrait). That fail direction is deliberate and asymmetric on purpose:
 * under-padding in real landscape hides controls under the status bar, while
 * over-padding in an unknown/undetectable state is merely cosmetic, so an
 * unknown state should always err toward MORE clearance, not less.
 */
export function detectPortrait(win = window) {
  if (!win) return true;
  if (typeof win.matchMedia === 'function') {
    return win.matchMedia('(orientation: portrait)').matches === true;
  }
  const height = typeof win.innerHeight === 'number' ? win.innerHeight : null;
  const width = typeof win.innerWidth === 'number' ? win.innerWidth : null;
  if (height !== null && width !== null) {
    return height >= width;
  }
  return true;
}

/**
 * Sets `--cvx-safe-top-floor` on `:root` from the detected platform/display
 * mode/orientation. Call synchronously before React mounts (src/index.js),
 * and again on every orientation change via `installSafeTopFloorWatcher`.
 * Total — never throws, still returns the computed px number if
 * `doc`/`doc.documentElement` is missing.
 */
export function applySafeTopFloor(doc = document, nav = window.navigator, win = window) {
  const px = computeSafeTopFloorPx({
    isIPhone: detectIPhone(nav),
    isStandalone: detectStandaloneDisplay(win),
    isPortrait: detectPortrait(win),
  });
  const canSetProperty = !!doc
    && !!doc.documentElement
    && !!doc.documentElement.style
    && typeof doc.documentElement.style.setProperty === 'function';
  if (canSetProperty) {
    doc.documentElement.style.setProperty(SAFE_TOP_FLOOR_VAR, `${px}px`);
  }
  return px;
}

/**
 * Re-applies the floor on every orientation settle cycle — required because
 * the floor is otherwise only written once at boot (`applySafeTopFloor` in
 * src/index.js). Orientation events are owned by viewportSafeAreaLayoutEpoch;
 * this watcher sets the settle preamble so the floor updates immediately on
 * rotate AND again after the settle window (before remount hooks). Returns an
 * unsubscribe that clears the preamble (epoch listeners stay for the app
 * lifetime).
 */
export function installSafeTopFloorWatcher(doc = document, nav = window.navigator, win = window) {
  if (!win) return () => {};

  ensureViewportSafeAreaLayoutEpochInstalled();
  setOrientationSettlePreamble(() => applySafeTopFloor(doc, nav, win));
  return () => {
    setOrientationSettlePreamble(null);
  };
}
